import { useEffect, useRef } from 'react';
import { useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import { Notification } from '../../hooks/useNotification';
import { TypeError } from '../../types/enums';

type SubmitValidationValues = {
  groupStandingsTieBreakers?: unknown;
};

type SubmitValidationNotifierProps = {
  onTieBreakerInvalid?: () => void;
};

/**
 * Formik bumps submitCount in SUBMIT_ATTEMPT before validation resolves, while
 * isValid is still the previous value. Remember the submit and check once
 * validation has finished.
 */
export const SubmitValidationNotifier = ({
  onTieBreakerInvalid,
}: SubmitValidationNotifierProps) => {
  const { submitCount, isValid, isValidating, errors } = useFormikContext<SubmitValidationValues>();
  const { t } = useTranslation();
  const lastSubmitCount = useRef(submitCount);
  const pendingSubmit = useRef(false);

  useEffect(() => {
    if (submitCount > lastSubmitCount.current) {
      pendingSubmit.current = true;
      lastSubmitCount.current = submitCount;
    }
    if (!pendingSubmit.current || isValidating) {
      return;
    }
    pendingSubmit.current = false;
    if (!isValid) {
      Notification(TypeError.error, t('validation-formHasErrors'));
      if (errors.groupStandingsTieBreakers) {
        onTieBreakerInvalid?.();
      }
    }
  }, [
    errors.groupStandingsTieBreakers,
    isValid,
    isValidating,
    onTieBreakerInvalid,
    submitCount,
    t,
  ]);

  return null;
};
